import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styles from './video-modal.module.css';
import { MediaItem } from '../../types';
import { getEmbedSrc } from '../../utils/media-utils';

interface VideoModalProps {
  item: MediaItem;
  onClose: () => void;
}

export default function VideoModal({ item, onClose }: VideoModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!item) return null;

  const embedSrc = getEmbedSrc(item.embedUrl || item.linkUrl);
  // Simple heuristic: if it's explicitly a "reel" type layout or the item type hints at vertical, use vertical aspect
  // However, often embeds like YouTube shorts can play in standard players or specialized ones.
  // The user specifically mentioned "small-reel-card", suggesting vertical content.
  const isVertical = item.layout === 'reel';

  return ReactDOM.createPortal(
    <div
      className={styles.overlay}
      ref={overlayRef}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      <div className={`${styles.contentContainer} ${isVertical ? styles.verticalContainer : ''}`}>
        {/* Close button outside for desktop, inside for mobile logic handled in CSS largely, but structurally here */}
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close video"
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className={`${styles.aspectRatioBox} ${isVertical ? styles.verticalAspect : ''}`}>
          <iframe
            src={embedSrc}
            title={item.title}
            className={styles.iframe}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </div>
    </div>,
    document.body
  );
}
